import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import StillButton from './ui/StillButton';
import { useOverlay } from '../App';
import { useEffect, useState } from 'react';

const PrivacyPolicyOverlay = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { toggleOverlay } = useOverlay();
    const [isMobile, setIsMobile] = useState(false);

    // Check for mobile view
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 800);
        };
        
        // Check immediately
        checkMobile();
        
        // Add resize listener
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Explicitly hide the header when this component mounts
    useEffect(() => {
        if (isOpen) {
            toggleOverlay(false);
        }
        
        // Ensure header remains hidden when component unmounts
        return () => toggleOverlay(false);
    }, [toggleOverlay, isOpen]);

    if (!isOpen) return null;

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
                staggerChildren: 0.1,
            },
        },
        exit: {
            opacity: 0,
            transition: {
                duration: 0.5,
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        },
        exit: {
            opacity: 0,
            y: 5,
            transition: {
                duration: 0.3
            }
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    return (
        <motion.div 
            className="fixed inset-0 bg-black bg-opacity-90 flex flex-col z-[250]"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
        >
            <motion.div 
                className={`w-full flex flex-col items-center ${isMobile ? 'p-4 pt-4' : 'mt-20 p-4 lg:mt-0 lg:p-8'}`}
                variants={containerVariants}
            >
                {/* Fixed title section */}
                <motion.div 
                    className={`w-full flex flex-col items-center p-4 ${isMobile ? 'mt-0 mb-4' : 'mt-20 mb-10'} max-w-3xl mx-auto`}
                    variants={itemVariants}
                >
                    <h1 className="text-center">
                        <span className="text-white text-glow-still">PRIVACY AND DATA PROTECTION POLICY</span>
                    </h1>
                </motion.div>
                
                {/* Scrollable content area */}
                <motion.div 
                    className={`w-full ${isMobile ? 'h-[calc(100vh-240px)]' : 'h-[calc(100vh-480px)]'} overflow-y-auto max-w-3xl mx-auto pb-32`}
                    variants={containerVariants}
                >
                    <motion.div 
                        className="w-full flex flex-col px-4 lg:px-10"
                        variants={containerVariants}
                    >
                        <motion.div 
                            className="space-y-6"
                            variants={itemVariants}
                        >
                            <div className="text-still-white text-base lg:text-base space-y-4">

                                <motion.p variants={itemVariants}>
                                    Still Loved is committed to protecting your privacy and personal data.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    Still Loved ("we", "our") is committed to protecting your personal data and your privacy. We encourage you to carefully read this Data Protection Policy ("Policy"). This Policy informs you of our data protection and privacy practices and the way your personal data is collected and used by Still Loved. This Policy is readily available on our home page and at the bottom of each page of this Website. Still Loved strongly supports the fundamental rights to privacy and data protection as well as compliance with national and international privacy laws.
                                    <br/><br/> 
                                    If you would like to edit your cookie settings, you can do so by clicking <a href="#" onClick={() => window.UC_UI.showSecondLayer()} className="text-still-pink">here</a>.
                                </motion.p>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    HOW WE PROCESS YOUR PERSONAL DATA
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    Still Loved respects the rights of each individual to have their privacy and personal data protected. Still Loved will observe the following principles when processing your personal data:
                                </motion.p>

                                <motion.ol className="list-decimal pl-6 space-y-2" variants={itemVariants}>
                                    <li>Processing your personal data fairly, lawfully and in a transparent manner;</li>
                                    <li>Collecting your personal data for specified, legitimate purposes and not processing further in ways incompatible with those purposes;</li>
                                    <li>Collecting your personal data which is relevant to and not excessive for the purposes for which it is collected and used. We may render personal data anonymous when feasible and appropriate, depending on the nature of the data and the risks associated with the intended uses;</li>
                                    <li>Maintaining your personal data and ensuring it is accurate, and where necessary, kept up-to-date. We will take reasonable steps to rectify or delete data that is inaccurate or incomplete;</li>
                                    <li>Keeping your personal data only as long as it is necessary for the purposes for which it was collected and processed;</li>
                                    <li>Processing your personal data in accordance with the individual's legal rights;</li>
                                    <li>Taking appropriate technical and organizational security measures to keep your data safe and prevent unauthorized access, unlawful processing, and unauthorized or accidental loss, destruction, or damage to personal data;</li>
                                    <li>Processing your personal data only if it is lawful in accordance with a legal basis including if you have unambiguously given your consent to such processing.</li>
                                </motion.ol>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    WHAT DATA IS COLLECTED?
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    In its capacity as data controller, Still Loved collects and processes your personal data after informing you accordingly. Personal data is any information relating to an identified or identifiable natural person. An identifiable person is a person who can be identified, directly or indirectly, in particular by reference to an identifier or to one or more factors specific to their physical, physiological, mental, economic, cultural or social identity.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    This Policy does not cover data rendered anonymous, i.e. when individuals are no longer identifiable or are identifiable only with a disproportionately large expense in time, cost and labour. If anonymous data becomes identifiable, or if pseudonyms are used and allow individuals to be identified, then this Policy will apply.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    It is up to you whether or not to disclose personal data to us. However, if you choose not to do so, we reserve the right not to register you as a user or not to provide you with the requested service.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    In general, your personal data is collected directly from you, but it may also be collected from third parties. The types of personal data we process include:
                                </motion.p>

                                <motion.ul className="list-disc pl-6 space-y-2" variants={itemVariants}>
                                    <li><strong>contact information:</strong> for example, your first name, last name, pseudonym, gender, email address, postal address, telephone number;</li>
                                    <li><strong>identification documents/data:</strong> for example, your passport details where we are arranging travel on your behalf, photographs, social media profiles;</li>
                                    <li><strong>your professional data:</strong> for example, your educational and professional background for job applicants, your job title, home company, or other application data for example where you apply for a programme/event;</li>
                                    <li><strong>your login data:</strong> your IP address, if applicable, your online ID, your Still Loved online user password;</li>
                                    <li><strong>competition data:</strong> for example, your name, contact details, location and, if applicable, proof of age;</li>
                                    <li><strong>your browsing data and expressed preference:</strong> traffic and browsing history on Still Loved websites, data from cookies and trackers on Still Loved websites; and</li>
                                    <li><strong>data related to your participation in any of our events and/or programmes:</strong> for example, biographical information, your qualifications, professional experience and employer, your image, audio and likeness (as captured on a webinar, in photographs or on recordings we make of the event, and on CCTV where the event is hosted at our premises), medical information (e.g. allergy information) relevant for your participation, your views/interests expressed whilst participating.</li>
                                </motion.ul>

                                <motion.p variants={itemVariants}>
                                    In general, and with some exceptions, the use of Still Loved websites and Still Loved pages on social networks is reserved for adults.
                                </motion.p>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    PURPOSE, LEGAL BASIS AND DATA RETENTION PERIOD
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    The processing operations carried out by Still Loved have the following purposes, legal bases and retention periods:
                                </motion.p>

                                <motion.div className="overflow-x-auto my-4" variants={itemVariants}>
                                    <table className="min-w-full bg-transparent border-collapse text-still-white text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-600">
                                                <th className="py-2 px-4 text-left">Purposes</th>
                                                <th className="py-2 px-4 text-left">Legal basis</th>
                                                <th className="py-2 px-4 text-left">Retention period</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Customer relationship management</td>
                                                <td className="py-2 px-4">Performance of the contract<br />Legal obligation (accounting/tax/administrative)</td>
                                                <td className="py-2 px-4">Term of the contract plus applicable limitation periods</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Partner and supplier relationship management</td>
                                                <td className="py-2 px-4">Performance of the contract<br />Legal obligation (accounting/tax/administrative)</td>
                                                <td className="py-2 px-4">Term of the contract plus applicable limitation periods</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Application management</td>
                                                <td className="py-2 px-4">Legitimate interest of Still Loved</td>
                                                <td className="py-2 px-4">2 years</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Product and institutional communication management (website and social networks)</td>
                                                <td className="py-2 px-4">Legitimate interest of Still Loved</td>
                                                <td className="py-2 px-4">Duration of the communication plus applicable limitation periods</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">B2B marketing</td>
                                                <td className="py-2 px-4">Legitimate interest of Still Loved<br />Consent</td>
                                                <td className="py-2 px-4">Duration of the related marketing activities plus applicable limitation periods</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Administrative and legal formalities and the fight against fraud</td>
                                                <td className="py-2 px-4">Legal and regulatory obligation</td>
                                                <td className="py-2 px-4">Duration of applicable legal requirements</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Subscription to newsletters</td>
                                                <td className="py-2 px-4">Consent and/or legitimate interests of Still Loved (as applicable, in accordance with laws)</td>
                                                <td className="py-2 px-4">Duration of subscription to the newsletter</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Management of cookies and other website trackers</td>
                                                <td className="py-2 px-4">Consent<br />Legitimate interest of Still Loved (technical cookies)</td>
                                                <td className="py-2 px-4">Periods specified in the website cookies policy</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Management and follow-up of contact requests</td>
                                                <td className="py-2 px-4">Legitimate interest of Still Loved</td>
                                                <td className="py-2 px-4">Time required to process the request</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Reporting and securing access to websites</td>
                                                <td className="py-2 px-4">Legitimate interest of Still Loved</td>
                                                <td className="py-2 px-4">6 months to 1 year maximum</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Exercising your personal data rights</td>
                                                <td className="py-2 px-4">Legal and regulatory obligation</td>
                                                <td className="py-2 px-4">1 year or 6 years from the request to exercise the right, according to the right exercised</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Participation in any of our events and/or programmes</td>
                                                <td className="py-2 px-4">Legitimate interest of Still Loved<br />Consent<br />Performance of the contract</td>
                                                <td className="py-2 px-4">Duration of the event/programme and related marketing activities plus applicable limitation periods</td>
                                            </tr>
                                            <tr className="border-b border-gray-700">
                                                <td className="py-2 px-4">Participation in any of our prize draws and competitions</td>
                                                <td className="py-2 px-4">Legitimate interest of Still Loved<br />Consent</td>
                                                <td className="py-2 px-4">Duration of the prize draw/competition and related marketing activities plus applicable limitation periods</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </motion.div>

                                <motion.p variants={itemVariants}>
                                    In the event of litigation/proceedings, particularly legal proceedings, initiated before the end of the above periods and which require the retention of personal data, particularly with a view to the establishment, exercise or defence of rights, such personal data shall be retained for the duration of said proceedings and until the exhaustion of the legal remedies.
                                </motion.p>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    WHO ARE THE RECIPIENTS OF YOUR DATA?
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    We may share your personal information within our group of companies.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    We may share your personal information with our suppliers, business partners and service providers, where they are helping us to market and advertise our services as well as where they are helping to provide services to you.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    For example, where you participate in an event/programme, we may share your personal data with the promotor/sponsor of the event/programme. In other cases and more generally, we may share your personal data with hosting providers of our websites, our auditors and statutory auditors, our legal advisers, our insurers, our duly authorized partners, service providers and subcontractors which we may use to carry out our activities, tax and social security bodies, public authorities, court officers and ministerial officers where applicable.
                                </motion.p>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    DATA TRANSFER
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    Your personal data collected and processed for the purposes described above may in certain cases be transmitted to companies outside of the UK, including countries, which have less strict, or no data protection laws, when compared to those in the EEA or the UK.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    Whenever we transfer your information as described in the paragraph above, we will take steps which are reasonably necessary to ensure that adequate safeguards are in place to protect your personal information and to make sure it is treated securely and in accordance with this privacy notice. In these cases, we rely on approved data transfer mechanisms (such as standard contractual clauses) to ensure your information is subject to adequate safeguards in the recipient country. If you are located in the UK or the EEA, you may contact us for a copy of the safeguards which we have put in place to protect your personal information and privacy rights in these circumstances.
                                </motion.p>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    LINKS TO THIRD-PARTY WEBSITES
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    Still Loved websites or services may provide links to third-party applications, products, services, or websites for your convenience and information. If you access those links, you will leave the Still Loved website. Still Loved does not control those third-party websites or their privacy practices, which may differ from our practices. We do not endorse or make any representations about third-party websites. The personal data you choose to provide to or that is collected by these third parties are not covered by this Policy. We encourage you to review the privacy policy of any website you interact with before allowing the collection and use of your personal data.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    We may also provide social media links that enable you to share data with your social networks and to interact with Still Loved on various social media sites. Your use of these links may result in the collection or sharing of data about you, depending on the feature. We encourage you to review the privacy policies and settings on the social media sites with which you interact to make sure you understand the data that may be collected, used, and shared by those sites.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    If you post, comment, indicate interest, or share personal data, including photographs, to any public forum, social network, blog, or other such forum, please be aware that any personal data you submit can be read, viewed, collected, or used by other users of these forums, and could be used to contact you, send you unsolicited messages, or for purposes that neither you nor Still Loved have control over. Still Loved is not responsible for the personal data you choose to submit in these forums.
                                </motion.p>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    YOUR DATA PROTECTION RIGHTS
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    You have certain rights in relation to your personal information, as listed below. We will handle any request to exercise your rights in accordance with applicable law and any relevant legal exemptions.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Right of access</strong><br />
                                    As data subjects you may inquire as to the nature of the personal data Still Loved holds about you. You will be provided access to your personal data regardless of the location of the data processing and storage.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Right to rectification</strong><br />
                                    If your personal data is inaccurate or incomplete, you may request that the data is amended.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Right to object</strong><br />
                                    You have the right to object at any time to the processing of your personal data by Still Loved.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Right to erasure</strong><br />
                                    You can ask for the deletion of your personal data when it is allowed by applicable laws and regulations.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Right to restriction of processing</strong><br />
                                    You have the right to request the processing of your personal data to be restricted when it is permitted by applicable laws and regulations.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Right to data portability</strong><br />
                                    If you meet the conditions settled by the applicable laws and regulations, you have the right to receive a subset of your personal data and to transmit it from Still Loved to another data controller.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Right to lodge a complaint</strong><br />
                                    You have the right to lodge a complaint with the competent supervisory authority. All requests for aforementioned rights, and any questions regarding this Data Protection Policy, may be sent to: Still Loved Data Protection privacy@stillloved.co.uk
                                </motion.p>

                                <motion.h3 className="text-white text-glow-still text-xl mt-6 mb-2" variants={itemVariants}>
                                    CHANGES TO THIS POLICY
                                </motion.h3>

                                <motion.p variants={itemVariants}>
                                    Still Loved reserves the right to modify this Policy as needed, for example, to comply with changes in laws, regulations, Still Loved practices and procedures, or requirements imposed by data protection authorities.
                                </motion.p>

                                <motion.p variants={itemVariants}>
                                    <strong>Last update: March 2025</strong>
                                </motion.p>
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </motion.div>
            
            {/* Close button - fixed at bottom with proper spacing */}
            <motion.div 
                className={`fixed bottom-0 left-0 right-0 flex justify-center space-x-4 pointer-events-auto ${isMobile ? 'mb-0' : 'mb-12 sm:mb-16 md:mb-20 lg:mb-24'} z-50 pt-16 py-4 bg-gradient-to-t from-black via-black to-transparent h-22`}
                variants={itemVariants}
            >
                <StillButton
                    onClick={handleClose}
                    aria-label="Close"
                    className="w-auto mb-6"
                >
                    CLOSE
                </StillButton>
            </motion.div>
        </motion.div>
    );
};

export default PrivacyPolicyOverlay; 